import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English is the only locale bundled into the entry chunk. Every other
// language is a dynamic import (see localeLoaders below), so the ~235 KB of
// translations for languages nobody can currently select is no longer shipped
// to every visitor on first paint.
import en from './en';

/**
 * Master switch for the multi-language UI.
 *
 * Set to `false` for the English-only launch. Side-effects when off:
 *   - LanguageSwitcher renders nothing (header dropdown is hidden)
 *   - i18n locks to 'en' regardless of browser locale or persisted choice
 *   - Any stale `i18nextLng` / `vowsera-lang` value in localStorage is wiped
 *     on next page load so a returning user who had picked Hindi/Chinese/etc.
 *     before the flag flipped doesn't see a half-translated UI
 *   - No locale chunk is ever fetched
 *
 * Flip back to `true` when (a) page-content strings are wrapped in t() and
 * (b) translations have had a native-speaker QA pass per language.
 */
export const LANGUAGES_ENABLED = false;

/**
 * i18n Configuration
 * Primary: English, Spanish, French
 * Secondary: Chinese, Korean, Portuguese, Hindi, Tamil, Gujarati
 */
export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇲🇽', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', dir: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', dir: 'ltr' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳', dir: 'ltr' },
];

/**
 * One dynamic import per non-English locale. Vite splits each into its own
 * chunk and fetches it only when that language is actually selected.
 */
const localeLoaders = {
  es: () => import('./es'),
  fr: () => import('./fr'),
  zh: () => import('./zh'),
  ko: () => import('./ko'),
  pt: () => import('./pt'),
  hi: () => import('./hi'),
  ta: () => import('./ta'),
  gu: () => import('./gu'),
};

/** 'en-US' -> 'en' */
const normalizeCode = (code) => String(code || 'en').split('-')[0].toLowerCase();

/**
 * Fetch and register a locale bundle on demand.
 *
 * Until the chunk resolves i18next falls back to English, which is the same
 * behaviour a missing key already had — so a slow network degrades to English
 * rather than to blank UI.
 */
export async function ensureLocale(lng) {
  const code = normalizeCode(lng);
  if (code === 'en') return;

  const load = localeLoaders[code];
  if (!load) return;
  if (i18n.hasResourceBundle(code, 'translation')) return;

  try {
    const mod = await load();
    i18n.addResourceBundle(code, 'translation', mod.default, true, true);

    // The bundle landed after the switch, so re-resolve to let react-i18next
    // re-render subscribers with the real strings. This re-fires
    // 'languageChanged', but the hasResourceBundle guard above makes the
    // second pass a no-op — no loop.
    if (i18n.language === code || i18n.resolvedLanguage === code) {
      await i18n.changeLanguage(code);
    }
  } catch {
    /* chunk failed to load — stay on the English fallback */
  }
}

// When a translation key is missing in BOTH the selected lang AND the EN
// fallback (e.g. a hardcoded `t('foo.bar')` whose key was never added to
// any lang file), parseMissingKeyHandler turns the last segment of the
// dotted key into Title Case so the UI shows "Bar" instead of the raw
// "foo.bar" path. This is a safety net while we migrate hardcoded
// English strings page-by-page; without it, missed keys leak into the UI.
const titleCaseFromKey = (key) => {
  const last = String(key || '').split('.').pop() || '';
  return last
    // camelCase → "camel Case"
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    // snake_case → "snake case"
    .replace(/_/g, ' ')
    // capitalise first letter of each word
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
};

// English-only launch: wipe any persisted lang choice so a returning user
// who picked Hindi/Chinese/etc. before the flag flipped doesn't get stuck
// on a half-translated UI. Safe to call unconditionally — the localStorage
// keys may not exist on a fresh browser.
if (!LANGUAGES_ENABLED && typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem('i18nextLng');
    window.localStorage.removeItem('vowsera-lang');
  } catch { /* private mode / disabled storage — ignore */ }
}

const i18nChain = LANGUAGES_ENABLED
  ? i18n.use(LanguageDetector).use(initReactI18next)
  : i18n.use(initReactI18next);

i18nChain.init({
    // Only English is preloaded. Everything else arrives via ensureLocale().
    resources: {
      en: { translation: en },
    },
    // When the multi-language UI is off we hardcode `lng: 'en'` so neither
    // the browser locale nor a persisted localStorage value can sneak a
    // non-English language back in. When it's on, we leave `lng` undefined
    // and let LanguageDetector resolve.
    ...(LANGUAGES_ENABLED ? {} : { lng: 'en' }),
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    parseMissingKeyHandler: titleCaseFromKey,
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

// Nothing to fetch while the UI is locked to English.
if (LANGUAGES_ENABLED) {
  i18n.on('languageChanged', (lng) => { ensureLocale(lng); });
  // The detector may have resolved to a non-English language before any
  // switch event fires, so cover the initial load too.
  ensureLocale(i18n.resolvedLanguage || i18n.language);
}

export default i18n;
